/**
 * #147 · Descarga de la página que se le entrega al impresor.
 *
 * Mismo patrón que `routes/analytics/download.ts`, y por el mismo motivo: la
 * ruta exige `Authorization`, y una navegación del navegador —`window.open`,
 * un `<a href>` directo— no manda el bearer. El token tampoco va en la URL:
 * terminaría en el historial y en el log de acceso del proxy.
 */
import { ApiError, asApiError } from '@/api/errors';
import type { ApiClient } from '@/api/client';

export interface SupplierToolDeps {
  api: ApiClient;
  fetchImpl?: typeof fetch;
  /** Sólo para tests: el `document` sobre el que se dispara el click. */
  doc?: Document;
}

/** Baja el archivo y lo entrega al navegador. Devuelve el nombre usado. */
export async function downloadSupplierTool(
  filename: string,
  deps: SupplierToolDeps,
): Promise<string> {
  const { api, fetchImpl = api.fetcher, doc = document } = deps;

  const session = api.currentSession();
  if (session === null) {
    throw new ApiError({ status: 401, code: 'no_session', message: 'No hay sesión iniciada.' });
  }

  let response: Response;
  try {
    // `api.resolve()` y no el path pelado: sin el prefijo del ambiente la
    // descarga cae en verify, que no conoce las rutas del panel (#140).
    response = await fetchImpl(api.resolve('/api/v1/backoffice/supplier-tool/download'), {
      headers: { Authorization: `Bearer ${session.accessToken}` },
      credentials: 'omit',
      redirect: 'error',
    });
  } catch (cause) {
    throw asApiError(cause);
  }

  if (!response.ok) {
    throw new ApiError({
      status: response.status,
      code: 'supplier_tool_download_failed',
      message: 'No se pudo descargar la herramienta del proveedor.',
      traceId: response.headers.get('X-Trace-Id'),
    });
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  try {
    const anchor = doc.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener';
    doc.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
  return filename;
}
